/**
 * MenuAIModal - Modal 'Montar Cardápio com IA' (upload de fotos/PDF -> IA extrai
 * categorias/produtos/adicionais). Extraído VERBATIM de ProductsContent.js
 * (Fase 2 refactor). Recebe estado/handlers via props do closure de ProductsContent.
 */
import React from 'react';
import { Camera, FileImage, FileText, X, Sparkles, Layers, Package, Plus, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const MenuAIModal = ({
  showMenuAIModal,
  setShowMenuAIModal,
  menuAIResult,
  setMenuAIResult,
  menuAIImages,
  setMenuAIImages,
  menuAIAnalyzing,
  menuAISaving,
  handleMenuAIFileSelect,
  removeMenuAIImage,
  analyzeMenuWithAI,
  editMenuAIItem,
  removeMenuAIItem,
  saveMenuAIResult,
}) => {
  return (
      <Dialog open={showMenuAIModal} onOpenChange={setShowMenuAIModal}>
        <DialogContent className="bg-gray-900 border-gray-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Camera size={24} className="text-green-400" />
              Montar Cardápio com IA
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Envie fotos ou PDFs do seu cardápio e a IA irá extrair automaticamente os produtos, preços e categorias.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Upload de arquivos */}
            {!menuAIResult && (
              <>
                <div className="border-2 border-dashed border-gray-700 rounded-xl p-6 text-center hover:border-green-500/50 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={handleMenuAIFileSelect}
                    className="hidden"
                    id="menu-ai-upload"
                  />
                  <label htmlFor="menu-ai-upload" className="cursor-pointer">
                    <FileImage size={40} className="mx-auto text-gray-500 mb-3" />
                    <p className="text-gray-300 font-medium">Clique para selecionar arquivos</p>
                    <p className="text-gray-500 text-sm mt-1">JPG, PNG, WEBP ou PDF (máx. 10 arquivos)</p>
                  </label>
                </div>
                
                {/* Preview das imagens selecionadas */}
                {menuAIImages.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-400">{menuAIImages.length} arquivo(s) selecionado(s):</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {menuAIImages.map((img, idx) => (
                        <div key={idx} className="relative group">
                          {img.type === 'application/pdf' ? (
                            <div className="aspect-square bg-gray-800 rounded-lg flex items-center justify-center">
                              <FileText size={32} className="text-red-400" />
                            </div>
                          ) : (
                            <img 
                              src={img.base64} 
                              alt={img.name}
                              className="aspect-square object-cover rounded-lg"
                            />
                          )}
                          <button
                            onClick={() => removeMenuAIImage(idx)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={14} className="text-white" />
                          </button>
                          <p className="text-xs text-gray-500 truncate mt-1">{img.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Botão de análise */}
                <button
                  onClick={analyzeMenuWithAI}
                  disabled={menuAIAnalyzing || menuAIImages.length === 0}
                  className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {menuAIAnalyzing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Analisando cardápio...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      Analisar com IA
                    </>
                  )}
                </button>
              </>
            )}
            
            {/* Resultado da análise */}
            {menuAIResult && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Resultado da Análise</h3>
                  <button
                    onClick={() => {
                      setMenuAIResult(null);
                      setMenuAIImages([]);
                    }}
                    className="text-sm text-gray-400 hover:text-white"
                  >
                    Nova análise
                  </button>
                </div>
                
                {/* Categorias */}
                {menuAIResult.categorias?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-green-400 flex items-center gap-2">
                      <Layers size={14} /> 
                      Categorias ({menuAIResult.categorias.length})
                    </h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {menuAIResult.categorias.map((cat, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-800/50 p-2 rounded-lg text-sm">
                          <input
                            type="text"
                            value={cat.nome}
                            onChange={(e) => editMenuAIItem('categorias', idx, 'nome', e.target.value)}
                            className="flex-1 bg-transparent border-none text-white focus:outline-none"
                          />
                          <button onClick={() => removeMenuAIItem('categorias', idx)} className="text-red-400 hover:text-red-300">
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Produtos */}
                {menuAIResult.produtos?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-blue-400 flex items-center gap-2">
                      <Package size={14} /> 
                      Produtos ({menuAIResult.produtos.length})
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {menuAIResult.produtos.map((prod, idx) => (
                        <div key={idx} className="bg-gray-800/50 p-3 rounded-lg space-y-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={prod.nome}
                              onChange={(e) => editMenuAIItem('produtos', idx, 'nome', e.target.value)}
                              className="flex-1 bg-gray-700 border-none text-white rounded px-2 py-1 text-sm"
                              placeholder="Nome"
                            />
                            <input
                              type="number"
                              value={prod.preco}
                              onChange={(e) => editMenuAIItem('produtos', idx, 'preco', parseFloat(e.target.value) || 0)}
                              className="w-24 bg-gray-700 border-none text-green-400 rounded px-2 py-1 text-sm text-right"
                              placeholder="Preço"
                              step="0.01"
                            />
                            <button onClick={() => removeMenuAIItem('produtos', idx)} className="text-red-400 hover:text-red-300">
                              <X size={14} />
                            </button>
                          </div>
                          <input
                            type="text"
                            value={prod.descricao || ''}
                            onChange={(e) => editMenuAIItem('produtos', idx, 'descricao', e.target.value)}
                            className="w-full bg-gray-700/50 border-none text-gray-300 rounded px-2 py-1 text-xs"
                            placeholder="Descrição"
                          />
                          <span className="text-xs text-gray-500">Categoria: {prod.categoria}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Adicionais */}
                {menuAIResult.adicionais?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-yellow-400 flex items-center gap-2">
                      <Plus size={14} /> 
                      Adicionais ({menuAIResult.adicionais.length})
                    </h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {menuAIResult.adicionais.map((add, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-800/50 p-2 rounded-lg text-sm">
                          <input
                            type="text"
                            value={add.nome}
                            onChange={(e) => editMenuAIItem('adicionais', idx, 'nome', e.target.value)}
                            className="flex-1 bg-transparent border-none text-white focus:outline-none"
                          />
                          <input
                            type="number"
                            value={add.preco}
                            onChange={(e) => editMenuAIItem('adicionais', idx, 'preco', parseFloat(e.target.value) || 0)}
                            className="w-20 bg-gray-700 border-none text-green-400 rounded px-2 py-1 text-xs text-right"
                            step="0.01"
                          />
                          <span className="text-xs text-gray-500">{add.grupo}</span>
                          <button onClick={() => removeMenuAIItem('adicionais', idx)} className="text-red-400 hover:text-red-300">
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Botões de ação */}
                <div className="flex gap-3 pt-4 border-t border-gray-800">
                  <button
                    onClick={() => setShowMenuAIModal(false)}
                    className="flex-1 px-4 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={saveMenuAIResult}
                    disabled={menuAISaving}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {menuAISaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        Importar Cardápio
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

  );
};

export default MenuAIModal;
